import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ScanLine, ArrowRight, Loader2, Crown, ImageOff, CalendarCheck, ShieldCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { CrownTimeline } from "../components/CrownTimeline";
import { ScoreRing } from "../components/ScoreRing";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { FRESHA_URL } from "../lib/constants";

const scoreColor = (s) => (s >= 75 ? "text-gold" : s >= 50 ? "text-cream" : "text-muted-foreground");

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scans, setScans] = useState(null);

  useEffect(() => {
    api.get("/scans").then((r) => setScans(r.data.scans)).catch(() => setScans([]));
  }, []);

  const last = scans && scans.length ? scans[0] : null;

  return (
    <div className="min-h-screen bg-charcoal text-cream">
      <Navbar />
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Header + current score */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-gold">Your Salon Dashboard</span>
            <h1 className="mt-2 flex flex-wrap items-center gap-3 font-heading text-4xl text-cream md:text-5xl">
              Hello, {user?.name?.split(" ")[0] || "friend"}.
              {user?.role === "admin" && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 px-3 py-1 font-sans text-xs uppercase tracking-[0.2em] text-gold" data-testid="admin-badge">
                  <ShieldCheck className="h-3.5 w-3.5" /> Admin
                </span>
              )}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {last ? `Your latest Crown Score is ${last.overall_score}/100 · ${last.overall_label}.` : "You haven't run a scalp scan yet. Let's start your first."}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button className="group rounded-full bg-gold px-6 py-5 text-charcoal hover:bg-gold/90" onClick={() => navigate("/payment")} data-testid="start-new-scan-btn">
                <ScanLine className="mr-2 h-4 w-4" /> SCAN NOW
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              {user?.role === "admin" && (
                <Button variant="ghost" className="rounded-full border border-gold/40 px-6 py-5 text-gold hover:text-cream" onClick={() => navigate("/admin")} data-testid="admin-manage-transformations">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Admin Console
                </Button>
              )}
              <a href={FRESHA_URL} target="_blank" rel="noopener noreferrer" data-testid="dashboard-book-consult">
                <Button variant="ghost" className="rounded-full border border-charcoal-border px-6 py-5 text-cream hover:text-gold">
                  <CalendarCheck className="mr-2 h-4 w-4" /> Book a consult
                </Button>
              </a>
            </div>
          </div>
          {last && (
            <div className="shrink-0"><ScoreRing score={last.overall_score} label={last.overall_label} size={150} /></div>
          )}
        </motion.div>

        {/* Journey timeline */}
        {scans && scans.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="mt-10">
            <CrownTimeline scans={scans} />
          </motion.div>
        )}

        {/* Recent scans */}
        <div className="mt-14">
          <h3 className="mb-6 flex items-center gap-2 font-heading text-2xl text-cream"><Crown className="h-5 w-5 text-gold" /> Recent scans</h3>
          {scans === null ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
          ) : scans.length === 0 ? (
            <div className="rounded-xl border border-dashed border-charcoal-border p-16 text-center text-muted-foreground" data-testid="empty-history">
              No scans yet — your reports will appear here to track progress over time.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="scan-history-grid">
              {scans.map((s, i) => (
                <motion.button key={s.scan_id}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.06 }}
                  onClick={() => navigate(`/report/${s.scan_id}`)}
                  className="group overflow-hidden rounded-xl border border-charcoal-border bg-charcoal-surface text-left transition-colors hover:border-gold/40"
                  data-testid={`scan-card-${s.scan_id}`}>
                  <div className="relative h-40 w-full overflow-hidden bg-charcoal">
                    {s.photo_base64 ? (
                      <img src={s.photo_base64} alt="scalp scan" className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full items-center justify-center text-muted-foreground"><ImageOff className="h-6 w-6" /></div>
                    )}
                    <div className="absolute right-3 top-3 rounded-full border border-white/10 bg-black/60 px-3 py-1 backdrop-blur-md">
                      <span className={`font-heading text-xl ${scoreColor(s.overall_score)}`}>{s.overall_score}</span>
                      <span className="text-xs text-cream/50">/100</span>
                    </div>
                    {s.source === "in_salon" && (
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full border border-gold/50 bg-black/60 px-2.5 py-1 text-[10px] text-gold backdrop-blur-md" data-testid={`in-salon-badge-${s.scan_id}`}>
                        <ShieldCheck className="h-3 w-3" /> In-Salon Scan
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between p-4">
                    <div>
                      <p className="text-sm text-cream">{new Date(s.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</p>
                      <p className="text-xs capitalize text-muted-foreground">{s.overall_label}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gold transition-transform group-hover:translate-x-0.5" data-testid={`view-report-link-${s.scan_id}`}>
                      View report <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
